import React, { useRef, useCallback, useEffect, memo, useState } from "react";
import { FormHelperText, Typography } from "@mui/material";
import classNames from "classnames";
import "./Profil.step.styles.scss";

interface AutoResizeTextareaProps {
  name: string;
  label: string;
  value: string;
  onChange: (name: string, value: string) => void;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  error?: boolean;
  helperText?: string;
  className?: string;
  debounceMs?: number;
}

const AutoResizeTextarea = memo(
  ({
    name,
    label,
    value,
    onChange,
    placeholder = "",
    required = false,
    maxLength = 3500,
    minRows = 2,
    maxRows = 6,
    error = false,
    helperText = "",
    className = "",
    debounceMs = 300,
  }: AutoResizeTextareaProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const debounceRef = useRef<NodeJS.Timeout>();

    // État local pour la valeur immédiate (sans latence)
    const [localValue, setLocalValue] = useState(value);
    const [isNearLimit, setIsNearLimit] = useState(false);

    // Synchroniser la valeur locale avec la prop value
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Fonction d'ajustement de hauteur optimisée
    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Utiliser requestAnimationFrame pour optimiser les performances
      requestAnimationFrame(() => {
        textarea.style.height = "auto";

        const lineHeight =
          parseInt(window.getComputedStyle(textarea).lineHeight) || 24;
        const minHeight = lineHeight * minRows + 24; // +24 pour le padding
        const maxHeight = lineHeight * maxRows + 24;
        const scrollHeight = textarea.scrollHeight;

        const newHeight = Math.min(
          Math.max(scrollHeight, minHeight),
          maxHeight
        );
        textarea.style.height = `${newHeight}px`;
      });
    }, [minRows, maxRows]);

    // Handler de changement avec debouncing
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;

        // Vérifier la limite de caractères
        if (newValue.length <= maxLength) {
          // Mise à jour immédiate de l'état local (pas de latence)
          setLocalValue(newValue);
          setIsNearLimit(newValue.length > maxLength * 0.85);

          // Ajuster la hauteur immédiatement
          setTimeout(adjustHeight, 0);

          // Debouncer l'appel au parent pour éviter les re-rendus fréquents
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }

          debounceRef.current = setTimeout(() => {
            onChange(name, newValue);
          }, debounceMs);
        }
      },
      [name, onChange, maxLength, adjustHeight, debounceMs]
    );

    // Ajuster la hauteur lors du montage et des changements de valeur
    useEffect(() => {
      adjustHeight();
    }, [localValue, adjustHeight]);

    // Gérer le redimensionnement de la fenêtre
    useEffect(() => {
      const handleResize = () => adjustHeight();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        // Nettoyer le timeout au démontage
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [adjustHeight]);

    return (
      <div className={classNames("text-area", className, { error })}>
        <Typography className="text-area-label">
          {label}
          {required && "*"}
          <span
            className={classNames("char-counter", {
              "near-limit": isNearLimit,
            })}
          >
            {localValue.length}/{maxLength}
          </span>
        </Typography>
        <textarea
          ref={textareaRef}
          className="text-area-input"
          name={name}
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={minRows}
        />
        {error && helperText && (
          <FormHelperText error>{helperText}</FormHelperText>
        )}
      </div>
    );
  }
);

AutoResizeTextarea.displayName = "AutoResizeTextarea";

export default AutoResizeTextarea;
